/**
 * Enrollment Hook
 * Student enrollment and lab access
 */

import { useState, useCallback } from 'react';
import { apiGet, apiPost, ApiError } from '../services/api';
import type { Enrollment } from '@cloak-next/shared';

interface EnrollmentWithClass extends Enrollment {
  className: string;
  classTitle?: string;
  shortClassId: string;
  labTypes: string[];
  status?: 'draft' | 'scheduled' | 'active' | 'ended';
}

interface JoinClassResponse {
  enrollment: Enrollment;
  class: {
    className: string;
    classTitle?: string;
    shortClassId: string;
    labTypes: string[];
  };
}

interface UseEnrollmentResult {
  enrollments: EnrollmentWithClass[];
  loading: boolean;
  error: string | null;
  fetchEnrollments: () => Promise<void>;
  joinClass: (classCode: string) => Promise<JoinClassResponse>;
}

/**
 * Hook for student enrollment
 */
export function useEnrollment(): UseEnrollmentResult {
  const [enrollments, setEnrollments] = useState<EnrollmentWithClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch student's enrollments
   * Uses GET /classes which returns enrolled classes with enrollment details for students
   */
  const fetchEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiGet<{ success: boolean; data: any[] }>('/classes');

      // Backend returns class data with enrollment fields (instanceId, enrolledAt) for students
      const enrollmentsData: EnrollmentWithClass[] = data.data.map((cls: any) => ({
        className: cls.className,
        classTitle: cls.classTitle,
        shortClassId: cls.shortClassId || '',
        instanceId: cls.instanceId, // Now provided by backend
        labTypes: cls.labTypes || [],
        status: cls.status,
        enrolledAt: cls.enrolledAt || cls.createdAt,
        studentEmail: '', // Not needed for display
        lastAccess: cls.lastAccess,
        ipAddress: cls.ipAddress,
      }));

      setEnrollments(enrollmentsData);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch enrollments';
      setError(message);
      console.error('Failed to fetch enrollments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Join a class with a class code
   */
  const joinClass = useCallback(async (classCode: string): Promise<JoinClassResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiPost<{ success: boolean; data: JoinClassResponse }>('/classes/join', { classCode });

      // Don't use optimistic updates - let the caller refresh enrollments
      // This avoids duplicate entries in the UI
      return response.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to join class';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    enrollments,
    loading,
    error,
    fetchEnrollments,
    joinClass,
  };
}
